import React, { useState, useEffect } from 'react';
import { Clock, Zap, ArrowRight, Check, AlertCircle, TrendingUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import subscriptionService from '../services/subscriptionService';
import Button from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { isOwner } from '../utils/rolePermissions';

const UpgradeCTA = ({ 
  variant = 'default', // 'default', 'compact', 'banner', 'inline'
  context = null, // 'trial-expiring', 'limit-reached', 'feature-locked'
  featureName = null,
  currentUsage = null,
  limit = null,
  className = ''
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trialStatus, setTrialStatus] = useState({ inTrial: false, daysRemaining: 0, trialEnd: null });
  const [upgradeRecommendation, setUpgradeRecommendation] = useState({ shouldUpgrade: false, recommendedPlan: null, reason: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUpgradeData();
  }, []);

  const loadUpgradeData = async () => {
    setLoading(true);
    const trialInfo = await subscriptionService?.isUserInTrial();
    const recommendation = await subscriptionService?.getUpgradeRecommendation();
    setTrialStatus(trialInfo);
    setUpgradeRecommendation(recommendation);
    setLoading(false);
  };

  const handleUpgrade = () => {
    navigate('/subscription-management');
  };

  const getContextualMessage = () => {
    // Context-specific messaging
    if (context === 'trial-expiring' && trialStatus?.inTrial) {
      return {
        title: `Trial ends in ${trialStatus?.daysRemaining} day${trialStatus?.daysRemaining !== 1 ? 's' : ''}`,
        message: 'Upgrade now to keep your data and unlock premium features',
        icon: <Clock className="w-6 h-6" />,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      };
    }

    if (context === 'limit-reached' && featureName) {
      return {
        title: `${featureName} limit reached`,
        message: 'Upgrade to Premium for unlimited access',
        icon: <AlertCircle className="w-6 h-6" />,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }

    if (context === 'feature-locked') {
      return {
        title: 'Premium Feature',
        message: 'Unlock advanced capabilities with Premium',
        icon: <Zap className="w-6 h-6" />,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      };
    }

    // Default upgrade message
    return {
      title: 'Upgrade to Premium',
      message: 'Get unlimited access to all features',
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    };
  };

  const getPlanBenefits = () => {
    return [
      'Doctor Consultation Summary',
      'Patient Notes',
      'Trigger Tracking',
      'Doctor Appointment Reminders'
    ];
  };

  const getPlanPrice = () => {
    return '£4.99';
  };

  if (loading) {
    return null;
  }

  // Hide for owner account - they have full access
  if (isOwner(user)) {
    return null;
  }

  // Don't show if no upgrade needed and no specific context
  if (!upgradeRecommendation?.shouldUpgrade && !context) {
    return null;
  }

  const contextMessage = getContextualMessage();
  const benefits = getPlanBenefits();
  const price = getPlanPrice();

  // Compact variant (for inline placement)
  if (variant === 'compact') {
    return (
      <div className={`flex items-center justify-between p-3 rounded-lg border ${contextMessage?.borderColor} ${contextMessage?.bgColor} ${className}`}>
        <div className="flex items-center gap-3">
          <div className={contextMessage?.color}>
            {contextMessage?.icon}
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{contextMessage?.title}</p>
            <p className="text-xs text-gray-600">{contextMessage?.message}</p>
          </div>
        </div>
        <Button
          onClick={handleUpgrade}
          size="sm"
          className="flex-shrink-0"
        >
          Upgrade
        </Button>
      </div>
    );
  }

  // Banner variant (for top of page)
  if (variant === 'banner') {
    return (
      <div className={`bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 ${className}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {contextMessage?.icon}
            <div>
              <p className="font-semibold">{contextMessage?.title}</p>
              <p className="text-sm text-blue-100">{contextMessage?.message}</p>
            </div>
          </div>
          <Button
            onClick={handleUpgrade}
            variant="outline"
            className="bg-white text-blue-600 hover:bg-gray-100 border-0"
          >
            View Plans
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Inline variant (minimal, text-based)
  if (variant === 'inline') {
    return (
      <button
        onClick={handleUpgrade}
        className={`text-blue-600 hover:text-blue-700 font-medium text-sm inline-flex items-center gap-1 ${className}`}
      >
        <Zap className="w-4 h-4" />
        Upgrade to unlock
        <ArrowRight className="w-3 h-3" />
      </button>
    );
  }

  // Default variant (full card)
  return (
    <div className={`bg-white rounded-lg border-2 ${contextMessage?.borderColor} shadow-lg overflow-hidden ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`${contextMessage?.color} ${contextMessage?.bgColor} rounded-full p-3`}>
            {contextMessage?.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {contextMessage?.title}
            </h3>
            <p className="text-gray-600">
              {contextMessage?.message}
            </p>
          </div>
        </div>

        {/* Usage indicator if provided */}
        {currentUsage !== null && limit !== null && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Current Usage</span>
              <span className="font-semibold text-gray-900">
                {currentUsage} / {limit}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min((currentUsage / limit) * 100, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Benefits */}
        <div className="mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">Upgrade benefits:</p>
          <div className="space-y-2">
            {benefits?.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="flex items-center justify-between mb-4 p-4 bg-blue-50 rounded-lg">
          <div>
            <p className="text-sm text-gray-600 mb-1">Starting at</p>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-gray-900">{price}</span>
              <span className="text-gray-600">/month</span>
            </div>
          </div>
          {trialStatus?.inTrial && (
            <div className="text-right">
              <p className="text-xs text-orange-600 font-semibold">TRIAL ENDS IN</p>
              <p className="text-2xl font-bold text-orange-600">{trialStatus?.daysRemaining}</p>
              <p className="text-xs text-gray-600">days</p>
            </div>
          )}
        </div>

        {/* CTA Button */}
        <Button
          onClick={handleUpgrade}
          className="w-full"
        >
          View All Plans
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <p className="text-center text-xs text-gray-500 mt-3">
          Cancel anytime • No hidden fees • Instant activation
        </p>
      </div>
    </div>
  );
};

export default UpgradeCTA;